import { api, type Id } from "@repo/db"
import { createFileRoute } from "@tanstack/react-router"

import { useConvex } from "@/hooks/use-convex"

export const Route = createFileRoute("/test")({
  component: TestPage,
})

function TestPage() {
  // Use the first dealership from our seed data
  const dealershipId = "k974p8wa6fm628q2j4a99ay3yx7jjxpe" as Id<"dealerships"> // Premium Motors Group

  // Test queries to verify connection and data
  const { data: settings } = useConvex(api.settings.get, { dealershipId })
  const { data: dealership } = useConvex(api.dealerships.queries.getBySlug, {
    slug: "premium-motors",
  })
  const { data: flags } = useConvex(api.featureFlags.list, { dealershipId })

  return (
    <div className="container mx-auto p-8">
      <h1 className="text-2xl font-bold mb-4">Convex Database Test</h1>

      <div className="grid gap-4">
        {/* Connection Status */}
        <div className="bg-gray-100 p-4 rounded">
          <h2 className="font-semibold mb-2">Connection Status</h2>
          <p>Database: {settings === undefined ? "Loading..." : "✅ Connected to dealer-db"}</p>
        </div>

        {/* Dealership Info */}
        {dealership && (
          <div className="bg-blue-50 p-4 rounded">
            <h2 className="font-semibold mb-2">Dealership: {dealership.name}</h2>
            <p className="text-sm text-gray-600">Slug: {dealership.slug}</p>
            {dealership.website && (
              <p className="text-sm text-gray-600">Website: {dealership.website}</p>
            )}
          </div>
        )}

        {/* Settings */}
        {settings && (
          <div className="bg-green-50 p-4 rounded">
            <h2 className="font-semibold mb-2">Dealership Settings</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium">Contact:</p>
                <p className="text-sm">{settings.contact?.phone}</p>
                <p className="text-sm">{settings.contact?.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Features:</p>
                <ul className="text-sm">
                  <li>Inventory: {settings.features?.inventory ? "✓" : "✗"}</li>
                  <li>Appointments: {settings.features?.appointments ? "✓" : "✗"}</li>
                  <li>Financing: {settings.features?.financing ? "✓" : "✗"}</li>
                  <li>Trade-In: {settings.features?.tradeIn ? "✓" : "✗"}</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Feature Flags */}
        {flags && flags.length > 0 && (
          <div className="bg-purple-50 p-4 rounded">
            <h2 className="font-semibold mb-2">Feature Flags</h2>
            <div className="grid grid-cols-2 gap-2">
              {flags.map((flag) => (
                <div key={flag._id} className="text-sm">
                  <span className={flag.enabled ? "text-green-600" : "text-gray-400"}>
                    {flag.enabled ? "✓" : "✗"} {flag.feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-yellow-50 rounded">
        <p className="text-sm text-gray-700">
          <strong>Test Data Summary:</strong> The database has been seeded with 3 dealerships, 5
          users, and test configurations. This is the new dealer-db instance, completely separate
          from the backend scraper.
        </p>
      </div>
    </div>
  )
}
